import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import cn from 'classnames';
import { ONBOARDING_SKIP_HOME_EVENT, ONBOARDING_STEP_EVENT } from '../../../constants/analyticEvents';
import { pushEvent } from '../../../utilities/analytics';
import { setDataV2, setCapacityTooltip, setCapacityScreens, setCurrentStep, setWizardDesktop, setMobileHomepage, hideOnboarding } from '../onboardingSlice';
import WizardButton from '../components/WizardButton';
import { getShowCapacityTotalButton, getWizardV2State } from '../wizardService';
import { isMobile } from '../../../utilities/helpers';
import WhoStepDesktop from '../../wizardDesktop/steps/WhoStepDesktop';
import abTesting from '../../../services/abTesting';
import { getParamFromUrl, isEntryFlow, removeParamFromUrl } from '../../../utilities/urlResolver';
import useGetWelcomeMessage from '../../../hooks/wizard/useGetWelcomeMessage';
import { t } from '../../../utilities/translator';
import { setLastChangedFilter } from '../../../appSlice';
import { setSecondaryFilterDefault } from '../../secondaryFilter/secondaryFilterSlice';
import { setMainFilterDefault } from '../../mainFilter/mainFilterSlice';
import showTrustBuilder from '../../../utilities/showTrustBuilder';

const WhoStep = () => {
  const dispatch = useDispatch();
  const { formV2: form } = useSelector(state => state.onboarding);
  const data = getWizardV2State().whoStep;
  const efMessageId = getParamFromUrl('pr_messageId');
  const [showCapacityTotalButton, setShowCapacityTotalButton] = useState(getShowCapacityTotalButton(form));
  const showSkipButton = !abTesting.welcomeScreen.hideSkip && localStorage.getItem('onboardingCompleted');

  const {
    subtitleShortText,
    isDestinationId,
    isDestination,
  } = useGetWelcomeMessage();

  const [showCustomMessage, setShowCustomMessage] = useState(!(efMessageId && isDestinationId));

  useEffect(() => {
    // for the entry-flow links: show custom message after destination name loading (if no welcome screen)
    if (efMessageId && isDestinationId && isDestination) {
      setShowCustomMessage(true);
    }
  }, [isDestinationId, isDestination]);

  const onCardClick = (item) => () => {
    if (item.id === form.who?.id) {
      dispatch(setDataV2({}));
    } else {
      let selection = item;
      if (['family', 'friends'].includes(item.id)) {
        selection = {
          id: item.id,
        }
      }
      dispatch(setDataV2({ who: selection }));
    }
    if (['family', 'friends'].includes(item.id)) {
      dispatch(setCapacityScreens(true));
    }

    pushEvent(ONBOARDING_STEP_EVENT, {
      step: 0,
      stepName: 'who',
      question: data.titleText,
      selection: item.id,
      skipAvailable: !!(localStorage.getItem('onboardingCompleted')),
    });

    setTimeout(() => {
      dispatch(setCurrentStep(1))
    }, 500)
  }

  const handleContinueClick = () => {
    dispatch(setCapacityScreens(!showCapacityTotalButton));
    if (showCapacityTotalButton) {
      dispatch(setCapacityTooltip(false));
    } else {
      localStorage.removeItem('hideCapacityTooltip');
    }
    dispatch(setCurrentStep(1))
  }

  const handleChangeCapacityClick = () => {
    dispatch(setDataV2({ who: {} }));
    setShowCapacityTotalButton(false);
  }

  const hideStaticWizard = () => {
    document.getElementById('mobile-preload-container')?.remove();
  };

  window.hideClick = () => {
    if (isEntryFlow('link-presets')) {
      window.location.hash = '';
    }
    dispatch(hideOnboarding());
    showTrustBuilder(dispatch);
    if (window.pr_welcome) {
      pushEvent(ONBOARDING_SKIP_HOME_EVENT, { welcomePresets: window.pr_welcomeScreenToLoad, skipFrom: 'welcome' });
    } else {
      pushEvent(ONBOARDING_SKIP_HOME_EVENT, { skipFrom: 'welcome' });
    }
    hideStaticWizard();
    const skeleton = document.querySelector('.skeleton-without-offers');
    skeleton?.classList?.add('hide-skeleton');

    const skeletonWithOffers = document.querySelector('.skeleton-with-offers');
    skeletonWithOffers?.classList?.add('hide-skeleton');
    dispatch(setWizardDesktop(false));
    dispatch(setMobileHomepage(false));
  };

  const onSkipClick = () => {
    window.hideClick();
    if (isEntryFlow('link-presets')) {
      removeParamFromUrl(['pr_entryFlow']);
    }
    document.querySelector('body').classList.remove('mobile-wizzard-active-mobhp');
    pushEvent(ONBOARDING_SKIP_HOME_EVENT, { skipFrom: 'welcome' });
    dispatch(setLastChangedFilter('what'));
    dispatch(setSecondaryFilterDefault());
    dispatch(setMainFilterDefault());
    dispatch(setWizardDesktop(false));
    dispatch(setMobileHomepage(false));
    document.dispatchEvent(new CustomEvent('wizard closed'));
  }

  const preSelectionComponent = () => {
    const adultsLabel = form.who.capacity?.adults === 1
      ? data.preselectAdult
      : data.preselectAdults.replace('%adults%', form.who.capacity?.adults);
    let childrenLabel = [];
    for (let i = 0; i < form.who.capacity?.children; i++) {
      childrenLabel.push(<span>{data.preselectChild.replace('%years%', form.who.capacity?.childAges?.[i] || 7)}</span>)
    }

    return (
      <div className="wizard-capacity-filled">
        <button className="wizard-guests-total-button">
          <span>{adultsLabel}</span>
          {form.who.capacity?.childAges?.length > 0 && childrenLabel}
        </button>
        <span className="wizard-capacity-label">{data.preselectText}</span>
        <button className="wizard-capacity-change-button" onClick={handleChangeCapacityClick}>{data.preselectButtonText}</button>
      </div>
    )
  }

  if (!isMobile()) {
    return (
      <WhoStepDesktop
        form={form}
        onCardClick={onCardClick}
        showCapacityTotalButton={showCapacityTotalButton}
        handleChangeCapacityClick={handleChangeCapacityClick}
        onContinueClick={handleContinueClick}
      />
    )
  }

  return (
    <div className="wizard-content-scrollable">
      <div className="wizard-step wizard-who-step">
        {abTesting.wizardLandingStepAlternative && efMessageId && subtitleShortText && (
          <div className={cn("mb-11 text-center text-shark-950 font-medium text-xl leading-6 welcome-custom-message welcome-cm-short", { "welcome-show-message": showCustomMessage })}>
            {subtitleShortText}
          </div>
        )}
        <div className="wizard-step__title">{data.titleText}</div>
        <div className={`wizard-step__options ${showCapacityTotalButton ? 'grid-cols-1' : 'grid-cols-2'}`}>
          {showCapacityTotalButton ?
            preSelectionComponent() : (
            data.items.map((item, index) => (
              <WizardButton
                key={index}
                label={item.text}
                iconUrl={item.img}
                className={`slide-in-bottom animation-delay-${index * 100}`}
                active={form.who?.id === item.id}
                onClick={onCardClick(item)}
              />
            ))
          )}
        </div>

        {abTesting.wizardLandingStepAlternative && (
          <button className={cn('welcome-screen-btn welcome-screen-btn__skip mt-8 text-primary heading-tiny leading-none', { 'is-hidden': !showSkipButton })} onClick={onSkipClick}>
            {t('onboarding.skipWizard')}
          </button>
        )}
      </div>

      {(form.who.capacity) && (
        <div className="wizard-footer">
          <button className="wizard-control-button" onClick={handleContinueClick}>{data.buttonText}</button>
        </div>
      )}
    </div>
  )
}

export default WhoStep
